import React from 'react';
import { kindMeta, CURSIVE, SAMPLE } from './constants';

// One placed block. The bottom edge is the signing line — the sample mark rests
// on it, matching esign/stamp.js and the agora signing page. Keep all three in
// step if this changes.
export default function FieldBox({ field, pageHeightPts, scale, selected, roleLabel, onSelect, onBeginDrag, onMoveBy, onResize }) {
  const drag = React.useRef(null);
  const startMove = (e) => {
    e.stopPropagation(); e.preventDefault();
    const additive = e.shiftKey || e.metaKey || e.ctrlKey;
    const ids = onSelect(field.id, additive);   // returns the new selection
    onBeginDrag(ids);                            // snapshot start positions
    drag.current = { mode: 'move', sx: e.clientX, sy: e.clientY };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const startResize = (e) => {
    e.stopPropagation(); e.preventDefault();
    onSelect(field.id, false);
    drag.current = { mode: 'resize', sx: e.clientX, sy: e.clientY, f: { ...field } };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    if (!drag.current) return;
    const dx = (e.clientX - drag.current.sx) / scale;
    const dy = (e.clientY - drag.current.sy) / scale;
    if (drag.current.mode === 'move') {
      onMoveBy(dx, dy); // moves the whole selection relative to its snapshot
    } else {
      const f0 = drag.current.f;
      const w = Math.max(24, f0.w + dx);
      const h = Math.max(14, f0.h + dy);
      onResize(field.id, { w, y: f0.y + f0.h - h, h });
    }
  };
  const end = () => { drag.current = null; };

  const meta = kindMeta(field.kind);
  const Icon = meta.icon;
  const isInk = field.kind === 'signature' || field.kind === 'initials';
  const left = field.x * scale;
  const top = (pageHeightPts - (field.y + field.h)) * scale;
  const w = field.w * scale;
  const h = field.h * scale;

  return (
    <div onPointerDown={startMove} onPointerMove={move} onPointerUp={end}
      style={{
        position: 'absolute', left, top, width: w, height: h, cursor: 'grab', boxSizing: 'border-box',
        border: `1.5px solid ${meta.color}`, borderBottom: `2.5px solid ${meta.color}`, borderRadius: 6,
        background: selected ? `${meta.color}26` : `${meta.color}14`,
        boxShadow: selected ? `0 0 0 3px ${meta.color}55, 0 2px 8px rgba(0,0,0,0.12)` : 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: selected ? 30 : 10,
        transition: 'box-shadow .12s, background .12s',
      }}>
      <div style={{ position: 'absolute', top: -18, left: -1, display: 'flex', alignItems: 'center', gap: 3,
        background: meta.color, color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 6px 1px 4px',
        borderRadius: '5px 5px 5px 0', whiteSpace: 'nowrap', pointerEvents: 'none', opacity: selected ? 1 : 0.9 }}>
        <Icon sx={{ fontSize: 12 }} /> {roleLabel(field.role)} · {meta.short}
      </div>
      <span style={{
        position: 'absolute', left: 3, right: 3, bottom: 0, textAlign: 'center', lineHeight: 1,
        fontFamily: isInk ? CURSIVE : 'inherit',
        fontSize: isInk ? Math.max(11, Math.min(30, h * 0.85)) : Math.max(8, Math.min(13, h * 0.62)),
        color: meta.color, fontWeight: isInk ? 400 : 600,
        pointerEvents: 'none', whiteSpace: 'nowrap', overflow: 'hidden', opacity: 0.65,
      }}>{SAMPLE[field.kind] || meta.short}</span>
      <div onPointerDown={startResize} onPointerMove={move} onPointerUp={end}
        style={{ position: 'absolute', right: -5, bottom: -5, width: 12, height: 12, background: '#fff', border: `2px solid ${meta.color}`, borderRadius: 3, cursor: 'nwse-resize' }} />
    </div>
  );
}
